import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import ApprovedPatientCard from '../../components/ApprovedPatientCard';
import { actionBtn, WebEntryLayout } from './entryHelpers';
import { patientAuthFlow } from '../../auth/patientAuthFlow';

export default function PatRegCardScreen() {
  const navigate = useNavigate();
  const [toast, setToast] = useState('');
  const { fullName, nhcId, bloodGroup, dob, gender } = patientAuthFlow.getState();

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const handleDownload = () => {
    window.print();
    showToast('Print dialog opened — save as PDF to download your NeoHealthCard');
    setTimeout(() => navigate('/patient/register/success'), 1000);
  };

  const handleShare = async () => {
    const shareData = {
      title: 'NeoHealthCard',
      text: `NeoHealthCard — Digital Health Identity\nName: ${fullName || 'Patient'}\nNHC ID: ${nhcId}\nBlood: ${bloodGroup || 'N/A'} | DOB: ${dob || 'N/A'} | Gender: ${gender || 'N/A'}\nVerified Patient · Generated through NeoHealthCard`,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.text);
        showToast('Card details copied to clipboard');
      }
    } catch {
      try {
        await navigator.clipboard.writeText(shareData.text);
        showToast('Card details copied to clipboard');
      } catch {
        showToast('Unable to share — please try again');
      }
    }
    setTimeout(() => navigate('/patient/register/success'), 1000);
  };

  return (
    <WebEntryLayout>
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: T.white,
        padding: '30px 20px',
        fontFamily: T.font,
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, textAlign: 'center' }}>Your NeoHealthCard</div>
      <div style={{ fontSize: 10, color: T.textSec, textAlign: 'center', marginBottom: 20 }}>Your digital health identity card</div>

      <ApprovedPatientCard
        name={fullName || 'Patient'}
        nhcId={nhcId}
        bloodGroup={bloodGroup}
        footer="Verified Patient · Every access is logged"
      />

      {/* QR + NFC badges */}
      <div style={{ display: 'flex', gap: 16, marginTop: 16, alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 6, height: 6, borderRadius: 3, background: T.success }} />
          <span style={{ fontSize: 9, fontWeight: 600, color: T.success }}>QR Enabled</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 6, height: 6, borderRadius: 3, background: T.success }} />
          <span style={{ fontSize: 9, fontWeight: 600, color: T.success }}>NFC Ready</span>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 10, marginTop: 24, width: '100%' }}>
        <button
          onClick={handleDownload}
          style={{
            ...actionBtn('Download Card', T.navy, '#FFF'),
            flex: 1,
            padding: '12px',
            borderRadius: 12,
            textAlign: 'center' as const,
          }}
        >
          Download Card
        </button>
        <button
          onClick={handleShare}
          style={{
            ...actionBtn('Share Card', T.teal, '#FFF'),
            flex: 1,
            padding: '12px',
            borderRadius: 12,
            textAlign: 'center' as const,
          }}
        >
          Share Card
        </button>
      </div>

      <div style={{ fontSize: 9, color: T.textSec, marginTop: 12, textAlign: 'center' }}>
        Generated through NeoHealthCard
      </div>

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: T.text, color: T.white, padding: '10px 20px', borderRadius: 10,
          fontSize: 12, fontFamily: T.font, fontWeight: 600, zIndex: 9999, boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        }}>
          {toast}
        </div>
      )}
    </div>
    </WebEntryLayout>
  );
}
